import React from 'react';
import PropTypes from 'prop-types';
import { getSortValueForFullName } from 'AppSrc/core/utility-functions/sort-functions';
import sortBy from 'lodash/sortBy';
import {
  getTimingTextForValue,
  getNetworkTextForValue,
  getDateDisplay,
  getRowItem,
  getCrqListRowId,
  getCrqListRowKey,
  getTimeLeft,
} from 'AppSrc/core/crq/crq-table-utils';
import { FIXED } from 'AppSrc/core/components/forms/value-dependency-constants';
import { internationalCoordinatorGroups } from 'AppSrc/core/components/forms/form-types/create-crq/section-one/section-one-constants';
import { PaginatedSortableListTable } from 'AppSrc/core/components/tables/paginated-sortable-list-table';
import { usernameSelector } from 'AppSrc/core/auth/auth-selectors';
import { useHistory } from 'react-router';
import { connect, useSelector } from 'react-redux';
import 'AppSrc/core/style/components/tables/vf-table.css';
import ReactTooltip from 'react-tooltip';
import {
  NEW_TAB_ID,
  PEER_TAB_ID,
  CIRCUITCHECK_TAB_ID,
  APPROVAL_TAB_ID,
  // REJECTED_TAB_ID,
  PRECLOSURE_TAB_ID,
  EMERGENCY_TAB_ID,
} from './coordinator-work-queue-constants';

const CRQS_PER_PAGE = 10;
const START_DATE_COLUMN = 'startDate';
const END_DATE_COLUMN = 'endDate';
const placeHolder = '-';

// column names for the possible columns
const columnNames = {
  [START_DATE_COLUMN]: 'Start Date',
  [END_DATE_COLUMN]: 'End Date',
  crqId: 'CRQ ID',
  network: 'Network',
  timing: 'SLA',
  requestorName: 'Requestor',
  coordinatorName: 'Coordinator',
  rejectedBy: 'Rejected by',
  approvalType: 'Approval type',
  analyst: 'Analyst',
  timeLeft: 'Time left',
  actionType: 'Action type',
  assigned: 'Assigned',
  crqSummary: 'Summary',
  circuitCheckAnalyst: 'Change Analyst',
};

const columnsWithNullLast = [
  START_DATE_COLUMN,
];

// first for columns for all tabs
const sharedColumnIds = [
  START_DATE_COLUMN,
  'crqId',
  'network',
  'timing',
];

// first three columns for att the tabs of ICM CRQs
const sharedIcmColumnIds = [
  START_DATE_COLUMN,
  'crqId',
  'timing',
];

// map of column Ids for tab
const extraColumnIdsByTab = {
  [CIRCUITCHECK_TAB_ID]: [
    'coordinatorName',
    'circuitCheckAnalyst',
    'crqSummary',
  ],
  [NEW_TAB_ID]: [
    'requestorName',
    'actionType',
    'crqSummary',
  ],
  [PEER_TAB_ID]: [
    'coordinatorName',
    'crqSummary',
  ],
  [APPROVAL_TAB_ID]: [
    'coordinatorName',
    'assigned',
    'crqSummary',
  ],
  [EMERGENCY_TAB_ID]: [
    'coordinatorName',
    'crqSummary',
  ],
  // [REJECTED_TAB_ID]: [
  //   'approvalType',
  //   'rejectedBy',
  // ],
  [PRECLOSURE_TAB_ID]: [
    END_DATE_COLUMN,
    'crqId',
    'network',
    'timeLeft',
    'actionType',
    'crqSummary',
  ],
  // [REJECTED_TAB_ID]: [
  //   'network',
  //   'approvalType',
  //   'rejectedBy',
  // ],
};

// extra column ids for ICM CRQs.
const extraColumnsIdsForIcmBytab = {
  [CIRCUITCHECK_TAB_ID]: [
    'coordinatorName',
    'circuitCheckAnalyst',
    'crqSummary',
  ],
  [NEW_TAB_ID]: [
    'requestorName',
    'actionType',
    'crqSummary',
  ],
  [PEER_TAB_ID]: [
    'coordinatorName',
    'crqSummary',
  ],
  [EMERGENCY_TAB_ID]: [
    'coordinatorName',
    'crqSummary',
  ],
  [APPROVAL_TAB_ID]: [
    'coordinatorName',
    'assigned',
    'crqSummary',
  ],
  [PRECLOSURE_TAB_ID]: [
    END_DATE_COLUMN,
    'crqId',
    // 'network',
    'timeLeft',
    'actionType',
    'crqSummary',
  ],
};

const noResultsMessages = {
  [NEW_TAB_ID]: 'There are no CRQs in this work queue.',
  [PEER_TAB_ID]: 'No CRQs to Peer Review',
  [CIRCUITCHECK_TAB_ID]: 'No CRQs requiring Circuit Check',
  [APPROVAL_TAB_ID]: 'No CRQs to Approve',
  // [REJECTED_TAB_ID]: 'No Rejected CRQs',
  [PRECLOSURE_TAB_ID]: 'No Completion reviews to complete for your CRQs',
  // [REJECTED_TAB_ID]: 'No Rejected CRQs',
  [EMERGENCY_TAB_ID]: 'There are no CRQs for L3 approval.',
};

const getColumnIdsForTab = (tab, userGroups) => {
  const hideNetworkCol = (userGroups[FIXED] && internationalCoordinatorGroups.includes(userGroups[FIXED][0])) ? true : false;
  const extraColumns = hideNetworkCol ? extraColumnsIdsForIcmBytab[tab] : extraColumnIdsByTab[tab];
  const sharedFilteredColumnIds = hideNetworkCol ? sharedIcmColumnIds : sharedColumnIds;
  return (tab === PRECLOSURE_TAB_ID ? [...extraColumns] : [
    ...sharedFilteredColumnIds,
    ...extraColumns,
  ]);
};

const getColumnsForTab = (tab, userGroups) => {
  const columnIds = getColumnIdsForTab(tab, userGroups);
  return columnIds.map((id) => {
    const name = columnNames[id];
    const sortWithNullLast = columnsWithNullLast.includes(id) ? true : false;
    return ({
      id,
      name,
      sortable: true,
      sortWithNullLast,
    });
  });
};

// TODO redo this based on the data returned from the API
const getRowForCrqAndTab = ({
  tab,
  crq,
  username,
  index,
  userGroups,
}) => {
  const {
    id: crqId,
    start_date: startDate,
    end_date: endDate,
    network,
    timing,
    requester_name: requestorName, // Mapping the new requester Id ELS Defect 9876
    coordinator_name: coordinatorName,
    peer_reviewer_name: rejectedBy,
    approval_type: approvalType,
    action_type: actionType,
    timeLeft,
    approver_assigned: assigned,
    status,
    status_reason: statusReason,
    adjacent_remedy_ref: adjacent,
    summary: crqSummary,
    circuit_check_analyst: circuitCheckAnalyst,
  } = crq;

  const defaultValues = {
    crqId,
    startDate,
    endDate,
    network,
    timing,
    requestorName,
    coordinatorName,
    rejectedBy,
    approvalType,
    actionType,
    assigned,
    circuitCheckAnalyst,
    timeLeft,
    status,
    statusReason,
    adjacent,
    crqSummary,
  };

  // formating date
  const startDateDisplay = getDateDisplay(startDate);
  const endDateDisplay = getDateDisplay(endDate);
  // Look up text values for id fields
  // Distinguish between Mobile CRQ and Mobile VINE CRQ in network column of HOME page table.
  let networkText = getNetworkTextForValue(network);
  const hasAdjRef = crq.hasOwnProperty('adjacent_remedy_ref');
  if (networkText === 'Mobile UK' && (adjacent !== null) && (hasAdjRef === true)) {
    networkText = 'Mobile(F)';
  }

  if (networkText === 'Mobile UK' && (adjacent === null)) {
    networkText = 'Mobile UK';
  }
  const timingText = getTimingTextForValue(network, `${timing}`);
  // hide the requester name if it is the same as the logged in user
  const requestorNameDisplay = requestorName === username ? '' : requestorName;
  const timeLeftText = getTimeLeft(status, endDate, network, actionType);
  const timeLeftDisplay = timeLeftText > 0 ? `${timeLeftText}h` : 'OVERDUE';
  const upperCaseActionType = actionType ? `${actionType.slice(0, 1).toUpperCase()}${actionType.slice(1)}` : '';
  // const ViewButton = <span className="view-button">View</span>;
  const actionTypeDom = (
    <div className="column-actiontype">
      <span>{upperCaseActionType}</span>
      {/* {ViewButton} */}
    </div>
  );
  // Added new column 'Summary' in the home dashboard CRQ table to view CRQ summary on the dashboard.
  const isSummary = (
    <div className="column-summary dashboard-summary">
      <span className="row-item-text" data-tip={crqSummary}>
        {crqSummary}
      </span>
      <ReactTooltip />
    </div>
  );
  const isCircuitCheckAnalyst = (
    <div className="column-circuitCheckAnalyst">
      <span>
        {circuitCheckAnalyst ? circuitCheckAnalyst : placeHolder}
      </span>
    </div>
  );

  // Display values for each column
  // mostly direct from the crq data,
  // but some columns are formateed
  const displayValues = {
    ...defaultValues,
    startDate: startDateDisplay,
    endDate: endDateDisplay,
    network: networkText,
    timing: timingText,
    timeLeft: timeLeftDisplay,
    requestorName: requestorNameDisplay,
    actionType: actionTypeDom,
    crqSummary: isSummary,
    circuitCheckAnalyst: isCircuitCheckAnalyst,
  };

  // sort values for the crq
  // mostly direct from the crq data
  // but some fields have specific sort values
  const sortValues = {
    ...defaultValues,
    network: networkText,
    timing: timingText,
    timeLeft: timeLeftText.toString(),
    coordinatorName: getSortValueForFullName(coordinatorName),
    requestorName: getSortValueForFullName(requestorName),
    rejectedBy: getSortValueForFullName(rejectedBy),
  };

  const columnIds = getColumnIdsForTab(tab, userGroups);
  const rowId = getCrqListRowId(network, crqId);
  const rowKey = getCrqListRowKey(crqId, index);
  const row = {
    id: rowId,
    key: rowKey,
    cells: [],
    sortValues: {},
  };

  // go through the visible columns and add in the row cells and the sort data
  columnIds.forEach((columnId, colIndex) => {
    const sortValue = sortValues[columnId];
    const displayText = displayValues[columnId];
    const lastColumn = colIndex === columnIds.length - 1;
    const cell = getRowItem({
      columnId,
      displayText,
      withChevron: tab === PRECLOSURE_TAB_ID ? lastColumn : lastColumn,
    });
    row.cells.push(cell);
    row.sortValues[columnId] = sortValue;
  });
  return row;
};

const initialSortedDataForPreclosureTab = (rows) => {
  const sortedData = sortBy(rows, ['sortValues.actionType', 'sortValues.timeLeft', 'sortValues.network', 'sortValues.crqId']);
  return sortedData;
};


const CoordinatorWorkQueueTableDisplay = ({
  tab,
  crqList,
  username,
}) => {
  const userGroups = useSelector((state) => state.auth.user.groups);
  const columns = getColumnsForTab(tab, userGroups);
  const isPreClosureTab = tab === PRECLOSURE_TAB_ID;
  const rows = crqList.map((crq, index) => getRowForCrqAndTab({
    tab,
    crq,
    username,
    index,
    userGroups,
  }));
  const sortedData = isPreClosureTab ? initialSortedDataForPreclosureTab(rows) : rows;
  const history = useHistory();
  const noResultsMessage = noResultsMessages[tab];
  const isApprovals = tab === APPROVAL_TAB_ID;
  const isDuplicate = true;
  return (
    <div className="coordinator-work-queue-table">
      <PaginatedSortableListTable
        key={tab} // guarantee re-render when changing tabs
        rows={sortedData}
        columns={columns}
        rowsPerPage={CRQS_PER_PAGE}
        onClickRow={(rowId) => { // rowId is E.G 'mobile/CRQ-000001'
          // for approvals tab, add `/approvals` to the url
          const path = `/crq/${rowId}${isApprovals ? '/approvals' : ''}`;
          history.push(path);
          window.localStorage.removeItem('isInfoItem');
        }}
        className={`homepage-crq-table homepage-crq-table-${tab}`}
        initialSortedColumn="startDate"
        initialSortDesc={false}
        noResultsMessage={noResultsMessage}
        isDuplicate={isDuplicate}
      />
    </div>
  );
};

CoordinatorWorkQueueTableDisplay.propTypes = {
  tab: PropTypes.string,
  crqList: PropTypes.arrayOf(PropTypes.object),
  username: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => ({
  // for now retrieve the username from the auth login
  // to compare to requester_name
  // TODO: change this to compare requester_id with logged in user id
  // once VTO-516 is complete
  username: usernameSelector(state),
});

const CoordinatorWorkQueueTable = connect(
  mapStateToProps,
)(CoordinatorWorkQueueTableDisplay);

export {
  CoordinatorWorkQueueTable,
  CoordinatorWorkQueueTableDisplay,
};
